// src/pages/CategoriesPage.tsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
} from '@mui/material';
import { fetchAllCategories } from '../store/slices/categoriesSlice';
import { AppDispatch, RootState } from '../store';

// 類別顏色映射
const getCategoryColor = (slug: string): string => {
  const customColors: Record<string, string> = {
    nature: '#2e7d32', // 深綠色
    human: '#1976d2', // 藍色
    history: '#9c27b0', // 紫色
    science: '#0288d1', // 淺藍色
    tech: '#0097a7', // 青色
    food: '#d32f2f', // 紅色
    travel: '#ed6c02', // 橙色
    architecture: '#7b1fa2', // 深紫色
    law: '#c2185b', // 洋紅色
    probability: '#1565c0', // 深藍色
    others: '#757575', // 灰色
  };

  return customColors[slug] || '#757575';
};

const CategoriesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { categories, loading, error } = useSelector(
    (state: RootState) => state.categories
  );

  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  if (loading && categories.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          無法載入類別: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        冷知識分類
      </Typography>

      <Typography variant="body1" paragraph>
        探索不同領域的冷知識，從科學到歷史，總有一個分類能勾起你的好奇心。
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
        {categories.map((category) => (
          <Box
            key={category.id}
            sx={{
              width: { xs: '100%', sm: '50%', md: '33.333%' },
              padding: 1.5,
            }}
          >
            <Link
              to={`/categories/${category.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6,
                  },
                  bgcolor: getCategoryColor(category.slug || ''),
                  color: 'white',
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  align="center"
                  gutterBottom
                >
                  {category.name}
                </Typography>

                {category.description && (
                  <Typography variant="body2" align="center">
                    {category.description}
                  </Typography>
                )}
              </Paper>
            </Link>
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default CategoriesPage;
